import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';
import GridContainer from '@/components/Grid/GridContainer';
import GridItem from '@/components/Grid/GridItem';
import CustomInput from '@/components/CustomInput/CustomInput';
import Card from '@/components/Card/Card';
import CardHeader from '@/components/Card/CardHeader';
import CardBody from '@/components/Card/CardBody';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  FormHelperText,
  Alert,
  Button,
} from '@mui/material';
import LoadingButton from '@/components/LoadingButton';
import { RefundsApi } from '../../../api/clients/refunds/refunds.api';
import { PaymentsApi } from '@/api/clients/payments/payments.api';
import { toast } from 'react-toastify';

import { BusinessUnitsApi } from '@/api/clients/business-units/business-units.api';
import ManageFiles from '@/views/Clients/ManageFiles';
import isAllowedForFacility, {
  APPROVAL_STATUS,
  ENTITY_TYPE,
  REFUND_MODAL_TYPE,
  REFUND_TYPES,
  CHECK_TYPE_DB,
  REFUND_PATH,
} from '@/utility';
import { useGetAllowedAttachmentTypes, useGetStates } from '@/api/hooks';
import { usePostHog } from 'posthog-js/react';
import { FacilitiesApi } from '@/api/clients/facilities';
import CountryDropdown from '@/components/CountryDropdown';
import { useGetClientBankAccountsForForms } from '@/api/hooks/client-bank-accounts/useGetClientBankAccountsForForms';
import { useGetCustomPaymentTypesForForm } from '@/api/hooks/custom-payments/useGetCustomPaymentTypesForForm';
import LineItemsSection from '@/components/LineItemsSection/LineItemsSection';
import {
  isFieldVisible,
  getFieldLabel,
  getAutoFillDefaults,
  AUTO_FILL_SCOPE,
} from '@/utils/fieldConfig';
import useLineItemAmountValidation from '@/hooks/useLineItemAmountValidation';
import CheckTypeSelector from '../CheckTypeSelector';
import CpdCheckboxCard from '@/components/ui/CpdCheckboxCard';
import { checkPermission } from '../CheckPermissions';
import SearchSuppliersModal from './SearchSuppliersModal';
import ConditionalInput from './ConditionalInput';
import { checkOrganizationFeatures } from '@/utility';
import { ORGANIZATION_FEATURES } from '@/utility';
import { normalizeFieldErrors } from '@/utils/validationErrors';
import { AudienceApi } from '@/api/clients/audience/audience.api';

const defaultFormValues = {
  facilityId: '',
  refundType: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  refundAmount: '',
  refundToAddressLine1: '',
  refundToAddressLine2: '',
  city: '',
  state: '',
  refundToZip: '',
  refundToCountry: '',
  generalLedger: '',
  memo: '',
  clientTransactionId: '',
  vendorIdNumber: '',
  payeeAccountNumber: '',
  businessUnitId: '',
  businessPayeeName: '',
  payImmediately: false,
  checkType: CHECK_TYPE_DB.PHYSICAL,
  clientBankAccountId: '',
  customPaymentTypeId: '',
  lineItems: [],
  audienceId: '',
};

const attentionRed = '#d32f2f';
const helperTextStyle = {
  fontWeight: 'bold',
  fontSize: '0.9em',
};
const errorHelperTextStyle = {
  color: attentionRed,
  fontWeight: 'bold',
  fontSize: '0.9em',
};
const asteriskStyle = {
  color: attentionRed,
  fontWeight: 'bold',
  fontSize: '1.4em',
  marginLeft: '2px',
};

const CreateOrEditManualRefund = ({
  refundLocation,
  refundId,
  onClose,
  updateStatus = false,
  editableFields = [],
  resendCommunicationUi = false,
  role = 'facility',
}) => {
  const { t } = useTranslation();
  const posthog = usePostHog();
  const {
    hasLineItemAmountTotalMismatch,
    lineItemAmountTotalMismatchMessage,
  } = useLineItemAmountValidation();
  const isApprovalPage = refundLocation === REFUND_PATH.APPROVALS;
  const isCustomPaymentTypesEnabled = posthog?.isFeatureEnabled(
    'new-custom-payment-types-workflow',
  );
  const isEcheckWorkflowEnabled = posthog?.isFeatureEnabled(
    'new-echeck-workflow',
  );
  const [refund, setRefund] = useState(null);
  const [isRefundLoading, setIsRefundLoading] = useState(false);
  const [formValues, setFormValues] = useState(defaultFormValues);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [attachments, setAttachments] = useState({
    internalAttachments: {
      addFiles: [],
      deletedFiles: [],
    },
    payeeAttachments: {
      addFiles: [],
      deletedFiles: [],
    },
  });
  const [isLoading, setIsLoading] = useState(false);
  const [allowedRefundTypes, setAllowedRefundTypes] = useState([]);
  const [availableFacilities, setAvailableFacilities] = useState([]);
  const [errors, setErrors] = useState({});
  const [isRefundTypesLoaded, setIsRefundTypesLoaded] = useState(false);
  const [comment, setComment] = useState('');
  const [resendCommunication, setResendCommunication] = useState(false);
  const [isEditable, setIsEditable] = useState(true);
  const [suppliersModalOpen, setSuppliersModalOpen] = useState(false);
  const [audiences, setAudiences] = useState([]);

  const showAudienceField =
    role === 'admin' || posthog?.isFeatureEnabled('new-audience-workflow');

  const { showAttachments } = useGetAllowedAttachmentTypes({
    refundType: formValues.refundType,
    facilityId: formValues?.facilityId,
    isCustomPaymentTypesEnabled,
  });
  const { states } = useGetStates(formValues.refundToCountry);
  const { customPaymentTypes } = useGetCustomPaymentTypesForForm(
    isCustomPaymentTypesEnabled,
    refundId,
    true,
  );
  const debounceTimer = useRef(null);
  const bankAccountInitialized = useRef(false);
  const bankAccountCleared = useRef(false);

  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  const refundModalType = useMemo(() => {
    if (refundLocation === REFUND_PATH.APPROVALS) {
      return REFUND_MODAL_TYPE.APPROVALS;
    } else if (refundLocation === REFUND_PATH.TRANSACTIONS) {
      return REFUND_MODAL_TYPE.REFUNDS_PAGE;
    } else if (
      refundLocation === REFUND_PATH.PROCESSING_EXCEPTIONS ||
      refundLocation === REFUND_PATH.COMMUNICATION_EXCEPTIONS
    ) {
      return REFUND_MODAL_TYPE.RESOLUTION_CENTER;
    } else {
      return REFUND_MODAL_TYPE.CREATE;
    }
  }, [refundLocation]);

  const selectedFacility = useMemo(() => {
    if (formValues.facilityId && availableFacilities.length > 0) {
      return availableFacilities.find((f) => f.id === formValues.facilityId);
    }
    return null;
  }, [formValues.facilityId, availableFacilities]);

  const selectedCustomPaymentType = useMemo(() => {
    if (formValues.customPaymentTypeId && customPaymentTypes.length > 0) {
      return customPaymentTypes.find(
        (type) => type.id === formValues.customPaymentTypeId,
      );
    }
    return null;
  }, [formValues.customPaymentTypeId, customPaymentTypes]);

  const organizationId = useMemo(() => {
    if (refund?.organization_id) {
      return refund.organization_id;
    }
    return selectedFacility?.organization_id || null;
  }, [refund, selectedFacility]);

  const facilityCurrency = selectedFacility?.country === 'CA' ? 'CAD' : 'USD';
  const shouldValidateLineItemAmount = useMemo(
    () =>
      isFieldVisible(
        'lineItem.amount',
        refundModalType,
        selectedCustomPaymentType,
      ),
    [refundModalType, selectedCustomPaymentType],
  );

  useEffect(() => {
    if (!selectedCustomPaymentType || refund) return;
    const defaults = getAutoFillDefaults(
      selectedCustomPaymentType,
      AUTO_FILL_SCOPE.TOP_LEVEL,
      { selectedFacility },
    );
    setFormValues((prev) => {
      const next = { ...prev };
      let changed = false;
      Object.entries(defaults).forEach(([key, value]) => {
        if (!(key in prev)) return;
        if (!next[key]) {
          next[key] = value;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [selectedCustomPaymentType, refund, selectedFacility]);

  const shouldShowBankAccount = selectedFacility?.is_fbo !== false;

  const {
    data: bankAccounts = [],
    loading: bankAccountsLoading,
  } = useGetClientBankAccountsForForms(organizationId, {
    enabled: Boolean(organizationId) && shouldShowBankAccount,
  });

  const activeBankAccounts = useMemo(() => {
    return bankAccounts.filter((account) => !account.deactivated_at);
  }, [bankAccounts]);

  const getError = (fieldName) => {
    return Boolean(errors[fieldName]?.messages?.length);
  };

  const getErrorMessage = (fieldName) => {
    return (
      errors[fieldName]?.messages
        ?.map((entry) => {
          if (typeof entry === 'string') {
            return entry;
          }
          if (entry?.code) {
            return t(`codes.${entry.code}`, { defaultValue: entry.message });
          }
          return entry?.message ?? '';
        })
        ?.join(', ') ?? ''
    );
  };

  const clearLineItemsError = useCallback(() => {
    setErrors((prevErrors) => {
      if (!prevErrors?.lineItems) {
        return prevErrors;
      }

      const nextErrors = { ...prevErrors };
      delete nextErrors.lineItems;
      return nextErrors;
    });
  }, []);

  useEffect(() => {
    const fetchRefund = async () => {
      if (!refundId) return;

      setIsRefundLoading(true);
      try {
        const response = await RefundsApi.getRefundWithLineItems(
          refundId,
          refundLocation,
        );

        setRefund(response.data);
      } catch (error) {
        console.error('Error fetching refund:', error);
        toast.error('Failed to load refund data');
        onClose?.();
      } finally {
        setIsRefundLoading(false);
      }
    };

    fetchRefund();
  }, [refundId]);

  useEffect(() => {
    const fetchFacilities = async () => {
      const facilities = await FacilitiesApi.getFacilitiesForForms();
      setAvailableFacilities(facilities);
    };

    fetchFacilities();
  }, []);

  useEffect(() => {
    const fetchAudiences = async () => {
      try {
        const response = await AudienceApi.getAudiencesForForm(
          role === 'admin',
          organizationId,
        );
        setAudiences(response.data || []);
      } catch (e) {
        console.error('Failed to fetch audiences', e);
      }
    };
    if (showAudienceField && organizationId) {
      fetchAudiences();
    }
  }, [showAudienceField, organizationId]);

  useEffect(() => {
    const fetchAllowedRefundTypes = async () => {
      try {
        const response = await RefundsApi.getAllowedRefundTypes(
          refund?.organization_id,
        );
        const refundTypes = response.data.allowed_refund_types || [];
        setAllowedRefundTypes(refundTypes);

        if (refundTypes.length === 1) {
          setFormValues((prevState) => ({
            ...prevState,
            refundType: refundTypes[0],
          }));
        }
      } catch (error) {
        console.error('Error fetching allowed refund types:', error);
      } finally {
        setIsRefundTypesLoaded(true);
      }
    };

    fetchAllowedRefundTypes();
  }, []);

  const buildFormData = (formValues, includeComment = false) => {
    const formData = new FormData();

    const refundFormValues = { ...formValues };
    if (businessUnits.length === 1) {
      refundFormValues.businessUnitId = businessUnits[0].id;
    }

    if (!shouldShowBankAccount) {
      delete refundFormValues.clientBankAccountId;
    }

    const lineItems = refundFormValues.lineItems;
    delete refundFormValues.lineItems;

    Object.keys(refundFormValues).forEach((key) => {
      formData.append(key, refundFormValues[key]);
    });

    if (lineItems && lineItems.length > 0) {
      formData.append('lineItems', JSON.stringify(lineItems));
    }

    attachments.internalAttachments.addFiles.forEach((file) => {
      if (file.local) {
        formData.append('internalFiles', file.file);
      }
    });
    if (attachments.internalAttachments.deletedFiles.length > 0) {
      formData.append(
        'deletedInternalFiles',
        JSON.stringify(attachments.internalAttachments.deletedFiles),
      );
    }

    attachments.payeeAttachments.addFiles.forEach((file) => {
      if (file.local) {
        formData.append('payeeFiles', file.file);
      }
    });
    if (attachments.payeeAttachments.deletedFiles.length > 0) {
      formData.append(
        'deletedPayeeFiles',
        JSON.stringify(attachments.payeeAttachments.deletedFiles),
      );
    }

    if (includeComment && refund) {
      formData.append('comment', comment);
    }

    return formData;
  };

  const handleAttachmentsChange = (newAttachments) => {
    setAttachments(newAttachments);
  };

  const handleChange = (event) => {
    if (!isEditable) return;
    const { name, value } = event.target;

    if (name === 'refundAmount') {
      clearLineItemsError();
    }

    setFormValues((prevState) => {
      let updated = { ...prevState, [name]: value };

      // Reset state and zip when country changes
      if (name === 'refundToCountry' && value !== prevState.refundToCountry) {
        updated = { ...updated, state: '', refundToZip: '' };
      }

      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      debounceTimer.current = setTimeout(() => {
        validateRefund(updated);
      }, 500);

      return updated;
    });
  };

  const handleLineItemsChange = (updatedLineItems) => {
    if (!isEditable) return;
    clearLineItemsError();
    setFormValues((prevState) => {
      const updated = { ...prevState, lineItems: updatedLineItems };

      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      debounceTimer.current = setTimeout(() => {
        validateRefund(updated);
      }, 500);

      return updated;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isLoading) return;

    if (refund && !comment.trim() && !isApprovalPage) {
      toast.error(t('createPayment.errors.commentRequired'));
      return;
    }

    if (
      shouldValidateLineItemAmount &&
      hasLineItemAmountTotalMismatch(
        formValues.lineItems,
        formValues.refundAmount,
      )
    ) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        lineItems: {
          messages: [lineItemAmountTotalMismatchMessage],
        },
      }));
      return;
    }

    clearLineItemsError();

    setIsLoading(true);
    setErrors([]);
    try {
      const formData = buildFormData(formValues, true);

      if (refund) {
        await PaymentsApi.updatePayment(
          refund,
          refundLocation,
          formData,
          updateStatus,
        );
      } else {
        await PaymentsApi.createPayment(formData);
      }

      if (!refund) {
        setFormValues(defaultFormValues);
      }

      if (resendCommunication && refund) {
        try {
          await RefundsApi.resendCommunication(refund.id);
        } catch (error) {
          console.error('Error resending communication:', error);
        }
      }

      onClose?.();
    } catch (error) {
      const fieldErrors = normalizeFieldErrors(error.response?.data);
      if (Object.keys(fieldErrors).length) {
        setErrors(fieldErrors);
      } else {
        console.error(error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const validateRefund = async (formValues) => {
    if (!refund) return;
    try {
      const formData = buildFormData(formValues, false);

      await RefundsApi.validateRefund(refund, formData, refundModalType);
      setErrors({});
    } catch (error) {
      const fieldErrors = normalizeFieldErrors(error.response?.data);
      if (error.response?.status === 400 && Object.keys(fieldErrors).length) {
        setErrors(fieldErrors);
      } else {
        console.error('Validation failed:', error.message);
      }
    }
  };

  useEffect(() => {
    if (!refund) return;

    const formValues = {
      facilityId: refund?.facility_id || '',
      refundType: refund?.refund_type || '',
      firstName: refund?.first_name || '',
      lastName: refund?.last_name || '',
      email: refund?.email || '',
      phone: refund?.phone || '',
      refundAmount: refund?.refund_amount || '',
      refundToAddressLine1: refund?.refund_to_address_line1 || '',
      refundToAddressLine2: refund?.refund_to_address_line2 || '',
      city: refund?.refund_to_city || '',
      state: refund?.refund_to_state || '',
      refundToZip: refund?.refund_to_zip || '',
      refundToCountry: refund?.refund_to_country || '',
      generalLedger: refund?.general_ledger || '',
      memo: refund?.memo || '',
      clientTransactionId: refund?.client_transaction_id || '',
      vendorIdNumber: refund?.vendor_id_number || '',
      payeeAccountNumber: refund?.payee_account_number || '',
      businessUnitId: refund?.business_unit_id || '',
      businessPayeeName: refund?.business_payee_name || '',
      payImmediately: refund?.pay_immediately || false,
      checkType: refund?.check_type || CHECK_TYPE_DB.PHYSICAL,
      clientBankAccountId: refund?.client_bank_account_id || '',
      customPaymentTypeId: refund?.custom_payment_type_id || '',
      lineItems: refund?.lineItems || [],
      audienceId: refund?.audience_id || '',
    };
    setFormValues(formValues);
    bankAccountInitialized.current = false;
    bankAccountCleared.current = false;

    validateRefund(formValues);
  }, [refund, refundId]);

  useEffect(() => {
    const fetchBusinessUnits = async () => {
      const businessUnits = await BusinessUnitsApi.getBusinessUnitsForForm(
        formValues?.facilityId,
      );
      setBusinessUnits(businessUnits);

      if (businessUnits.length === 1 && !formValues.businessUnitId) {
        setFormValues((prevState) => ({
          ...prevState,
          businessUnitId: businessUnits[0].id,
        }));
      }
    };

    if (formValues?.facilityId) {
      fetchBusinessUnits();
    }
  }, [formValues?.facilityId]);

  useEffect(() => {
    if (
      activeBankAccounts.length === 1 &&
      !formValues.clientBankAccountId &&
      !bankAccountInitialized.current
    ) {
      bankAccountInitialized.current = true;
      setFormValues((prevState) => ({
        ...prevState,
        clientBankAccountId: activeBankAccounts[0].id,
      }));
    }
  }, [activeBankAccounts.length, formValues.clientBankAccountId]);

  useEffect(() => {
    if (
      !shouldShowBankAccount &&
      formValues.clientBankAccountId &&
      !bankAccountCleared.current
    ) {
      bankAccountCleared.current = true;
      setFormValues((prev) => ({
        ...prev,
        clientBankAccountId: '',
      }));
    } else if (shouldShowBankAccount) {
      bankAccountCleared.current = false;
    }
  }, [shouldShowBankAccount]);

  useEffect(() => {
    if (refund) {
      const approvalStatus = refund.approval_status;

      if (
        approvalStatus === APPROVAL_STATUS.WAITING_FOR_APPROVAL &&
        isApprovalPage
      ) {
        setIsEditable(false);
        return;
      }

      if (
        !(
          approvalStatus === APPROVAL_STATUS.WAITING_FOR_APPROVAL ||
          approvalStatus === APPROVAL_STATUS.DECLINED
        ) ||
        !isApprovalPage
      ) {
        return setIsEditable(true);
      }

      const userEmail = localStorage.getItem('loginEmail');
      if (
        refund.approval_requested_by ||
        (refund.approval_requested_by &&
          userEmail !== refund.approval_requested_by)
      ) {
        setIsEditable(false);
      } else {
        setIsEditable(true);
      }
    }
  }, [refund]);

  const isPayeeNameEditable = useMemo(() => {
    return checkPermission({
      parentKey: 'transaction_page',
      childKey: 'payee_names',
      permission: 'write',
      facilityId: refund?.facility_id,
    });
  }, [refund]);

  const isEditableField = useCallback(
    (fieldName) => {
      const isTransactionPage = refundLocation === REFUND_PATH.TRANSACTIONS;
      const payeeNames = ['firstName', 'lastName', 'businessPayeeName'];
      if (
        isTransactionPage &&
        !isPayeeNameEditable &&
        payeeNames.includes(fieldName)
      ) {
        return false;
      }
      if (editableFields.length === 0) {
        return isEditable;
      }
      return isEditable && editableFields.includes(fieldName);
    },
    [editableFields, isEditable],
  );

  if (refundId && isRefundLoading) {
    return (
      <GridContainer>
        <GridItem xs={12} sm={12} md={12}>
          <Card>
            <CardBody
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '400px',
              }}>
              <CircularProgress />
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <GridContainer>
        <GridItem xs={12} sm={12} md={12}>
          <Card>
            <CardHeader color='primary'>
              <h4>
                {refund && isApprovalPage
                  ? t('createPayment.title.view')
                  : refund
                  ? t('createPayment.title.edit')
                  : t('createPayment.title.create')}
              </h4>
            </CardHeader>
            <div
              style={{
                maxHeight: '84vh',
                overflow: 'auto',
              }}>
              <CardBody style={{ position: 'relative' }}>
                {!isEditable && refund && isApprovalPage && (
                  <div
                    style={{
                      background: '#ffeaea',
                      color: '#d32f2f',
                      padding: '12px 16px',
                      borderRadius: '4px',
                      marginBottom: '16px',
                      fontWeight: 'bold',
                      fontSize: '1.1em',
                    }}>
                    {t('createPayment.warnings.notEditable')}
                  </div>
                )}
                <GridContainer>
                  <GridItem xs={12} sm={12} md={6}>
                    <div className='custom-select'>
                      <FormControl
                        variant='standard'
                        error={getError('facilityId')}
                        disabled={!isEditableField('facilityId') || refund}>
                        <InputLabel id='facilityId'>
                          <>
                            {t('createPayment.fields.facility')}
                            <span style={asteriskStyle}>*</span>
                          </>
                        </InputLabel>
                        <Select
                          labelId='facilityId'
                          value={formValues.facilityId || ''}
                          onChange={handleChange}
                          name='facilityId'
                          className='full-width-elem'>
                          {availableFacilities.map((facility) => (
                            <MenuItem
                              key={facility.id}
                              disabled={
                                !isAllowedForFacility({
                                  parentKey: 'create_payment',
                                  childKey: 'create_payment',
                                  permission: 'write',
                                  facilityId: facility.id,
                                })
                              }
                              value={facility.id}>
                              {facility.name}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText style={helperTextStyle}>
                          {getErrorMessage('facilityId')}
                        </FormHelperText>
                      </FormControl>
                    </div>
                  </GridItem>
                  <GridItem xs={12} sm={12} md={6}>
                    <div className='custom-select'>
                      <FormControl
                        variant='standard'
                        error={getError(
                          isCustomPaymentTypesEnabled
                            ? 'customPaymentTypeId'
                            : 'refundType',
                        )}
                        disabled={
                          !isEditableField('refundType') ||
                          !formValues?.facilityId
                        }>
                        <InputLabel id='refund-type-label'>
                          <>
                            {t('createPayment.fields.paymentCategory')}
                            <span style={asteriskStyle}>*</span>
                          </>
                        </InputLabel>
                        {isCustomPaymentTypesEnabled ? (
                          <Select
                            labelId='refund-type-label'
                            value={formValues.customPaymentTypeId || ''}
                            onChange={handleChange}
                            name='customPaymentTypeId'
                            className='full-width-elem'>
                            {customPaymentTypes.map((type) => (
                              <MenuItem key={type.id} value={type.id}>
                                {type.name}
                              </MenuItem>
                            ))}
                          </Select>
                        ) : isRefundTypesLoaded ? (
                          allowedRefundTypes.length > 0 ? (
                            <Select
                              labelId='refund-type-label'
                              value={formValues.refundType || ''}
                              onChange={handleChange}
                              name='refundType'
                              className='full-width-elem'>
                              {allowedRefundTypes.includes('regular') && (
                                <MenuItem value='regular'>
                                  {t(
                                    'createPayment.paymentTypes.consumerPayment',
                                  )}
                                </MenuItem>
                              )}
                              {allowedRefundTypes.includes('insurance') && (
                                <MenuItem value='insurance'>
                                  {t(
                                    'createPayment.paymentTypes.insurancePayment',
                                  )}
                                </MenuItem>
                              )}
                              {allowedRefundTypes.includes('business') && (
                                <MenuItem value='business'>
                                  {t(
                                    'createPayment.paymentTypes.businessPayment',
                                  )}
                                </MenuItem>
                              )}
                            </Select>
                          ) : (
                            <p style={{ color: 'red' }}>
                              {t('createPayment.errors.noPaymentTypes')}
                            </p>
                          )
                        ) : (
                          <CircularProgress size={20} />
                        )}

                        <FormHelperText style={helperTextStyle}>
                          {getErrorMessage(
                            isCustomPaymentTypesEnabled
                              ? 'customPaymentTypeId'
                              : 'refundType',
                          )}
                        </FormHelperText>
                      </FormControl>
                    </div>
                  </GridItem>
                </GridContainer>

                {((isCustomPaymentTypesEnabled
                  ? formValues.customPaymentTypeId
                  : formValues.refundType) ||
                  refund) && (
                  <>
                    <GridContainer></GridContainer>
                    {checkOrganizationFeatures(
                      ORGANIZATION_FEATURES.showSupplierLookupModal,
                    ) &&
                      refundModalType === REFUND_MODAL_TYPE.CREATE && (
                        <GridContainer>
                          <GridItem
                            xs={12}
                            sm={12}
                            md={6}
                            sx={{ display: 'flex', alignItems: 'flex-end' }}>
                            <Button
                              variant='outlined'
                              onClick={() => setSuppliersModalOpen(true)}
                              disabled={!isEditable}>
                              {t('createPayment.buttons.searchSuppliers')}
                            </Button>
                          </GridItem>
                        </GridContainer>
                      )}
                    <GridContainer>
                      {(isCustomPaymentTypesEnabled
                        ? selectedCustomPaymentType?.entityType ===
                            ENTITY_TYPE.BUSINESS ||
                          selectedCustomPaymentType?.entityType ===
                            ENTITY_TYPE.UNKNOWN
                        : formValues.refundType !== REFUND_TYPES.REGULAR) && (
                        <GridItem xs={12} sm={12} md={6}>
                          <CustomInput
                            labelText={
                              <>
                                {isCustomPaymentTypesEnabled &&
                                selectedCustomPaymentType?.entityType ===
                                  ENTITY_TYPE.UNKNOWN
                                  ? t('createPayment.fields.payeeName')
                                  : t('createPayment.fields.businessPayeeName')}
                                <span style={asteriskStyle}>*</span>
                              </>
                            }
                            id='businessPayeeName'
                            name='businessPayeeName'
                            value={formValues.businessPayeeName || ''}
                            onChange={handleChange}
                            maxLength={60}
                            formControlProps={{ fullWidth: true }}
                            error={getError('businessPayeeName')}
                            inputProps={{
                              helperText: getErrorMessage('businessPayeeName'),
                            }}
                            helperTextStyle={helperTextStyle}
                            disabled={!isEditableField('businessPayeeName')}
                          />
                        </GridItem>
                      )}
                      {(!isCustomPaymentTypesEnabled ||
                        selectedCustomPaymentType?.entityType !==
                          ENTITY_TYPE.UNKNOWN) && (
                        <GridItem xs={12} sm={12} md={6}>
                          <CustomInput
                            labelText={
                              <>
                                {t('createPayment.fields.firstName')}
                                {(isCustomPaymentTypesEnabled
                                  ? selectedCustomPaymentType?.entityType ===
                                    ENTITY_TYPE.INDIVIDUAL
                                  : formValues.refundType ===
                                    REFUND_TYPES.REGULAR) && (
                                  <span style={asteriskStyle}>*</span>
                                )}
                              </>
                            }
                            id='firstName'
                            name='firstName'
                            value={formValues.firstName}
                            onChange={handleChange}
                            formControlProps={{ fullWidth: true }}
                            error={getError('firstName')}
                            inputProps={{
                              helperText: getErrorMessage('firstName'),
                            }}
                            helperTextStyle={helperTextStyle}
                            disabled={!isEditableField('firstName')}
                          />
                        </GridItem>
                      )}
                      {(!isCustomPaymentTypesEnabled ||
                        selectedCustomPaymentType?.entityType !==
                          ENTITY_TYPE.UNKNOWN) && (
                        <GridItem xs={12} sm={12} md={6}>
                          <CustomInput
                            labelText={
                              <>
                                {t('createPayment.fields.lastName')}
                                {(isCustomPaymentTypesEnabled
                                  ? selectedCustomPaymentType?.entityType ===
                                    ENTITY_TYPE.INDIVIDUAL
                                  : formValues.refundType ===
                                    REFUND_TYPES.REGULAR) && (
                                  <span style={asteriskStyle}>*</span>
                                )}
                              </>
                            }
                            id='lastName'
                            name='lastName'
                            value={formValues.lastName}
                            onChange={handleChange}
                            formControlProps={{ fullWidth: true }}
                            error={getError('lastName')}
                            inputProps={{
                              helperText: getErrorMessage('lastName'),
                            }}
                            helperTextStyle={helperTextStyle}
                            disabled={!isEditableField('lastName')}
                          />
                        </GridItem>
                      )}
                      <GridItem xs={12} sm={12} md={6}>
                        <CustomInput
                          labelText={t('createPayment.fields.email')}
                          id='email'
                          name='email'
                          value={formValues.email}
                          onChange={handleChange}
                          formControlProps={{ fullWidth: true }}
                          error={getError('email')}
                          inputProps={{
                            helperText:
                              getErrorMessage('email') ??
                              t('createPayment.validation.emailHelper'),
                          }}
                          helperTextStyle={helperTextStyle}
                          disabled={!isEditableField('email')}
                        />
                      </GridItem>
                    </GridContainer>
                    <GridContainer>
                      <GridItem xs={12} sm={12} md={6}>
                        <CustomInput
                          labelText={t('createPayment.fields.phone')}
                          id='phone'
                          name='phone'
                          value={formValues.phone}
                          onChange={handleChange}
                          formControlProps={{ fullWidth: true }}
                          error={getError('phone')}
                          inputProps={{
                            helperText: getErrorMessage('phone'),
                          }}
                          helperTextStyle={helperTextStyle}
                          disabled={!isEditableField('phone')}
                        />
                      </GridItem>
                    </GridContainer>
                    <GridContainer>
                      <GridItem xs={12} sm={12} md={6}>
                        <CustomInput
                          labelText={t('createPayment.fields.addressLine1')}
                          id='refundToAddressLine1'
                          name='refundToAddressLine1'
                          value={formValues.refundToAddressLine1}
                          onChange={handleChange}
                          formControlProps={{ fullWidth: true }}
                          error={getError('refundToAddressLine1')}
                          inputProps={{
                            helperText: getErrorMessage('refundToAddressLine1'),
                          }}
                          helperTextStyle={helperTextStyle}
                          disabled={!isEditableField('refundToAddressLine1')}
                        />
                      </GridItem>
                      <GridItem xs={12} sm={12} md={6}>
                        <CustomInput
                          labelText={t('createPayment.fields.addressLine2')}
                          id='refundToAddressLine2'
                          name='refundToAddressLine2'
                          value={formValues.refundToAddressLine2}
                          onChange={handleChange}
                          formControlProps={{ fullWidth: true }}
                          error={getError('refundToAddressLine2')}
                          inputProps={{
                            helperText: getErrorMessage('refundToAddressLine2'),
                          }}
                          helperTextStyle={helperTextStyle}
                          disabled={!isEditableField('refundToAddressLine2')}
                        />
                      </GridItem>
                    </GridContainer>
                    <GridContainer>
                      <GridItem xs={12} sm={12} md={6}>
                        <CustomInput
                          labelText={t('createPayment.fields.city')}
                          id='city'
                          name='city'
                          value={formValues.city}
                          onChange={handleChange}
                          formControlProps={{ fullWidth: true }}
                          error={getError('city')}
                          inputProps={{
                            helperText: getErrorMessage('city'),
                          }}
                          helperTextStyle={helperTextStyle}
                          disabled={!isEditableField('city')}
                        />
                      </GridItem>
                      <GridItem xs={12} sm={12} md={6}>
                        <CountryDropdown
                          name='refundToCountry'
                          value={formValues.refundToCountry || ''}
                          onChange={handleChange}
                          error={getError('refundToCountry')}
                          disabled={!isEditableField('refundToCountry')}
                          errorMessage={getErrorMessage('refundToCountry')}
                          helperTextStyle={helperTextStyle}
                        />
                      </GridItem>
                    </GridContainer>
                    <GridContainer>
                      <GridItem xs={12} sm={12} md={6}>
                        <div className='custom-select'>
                          <FormControl
                            variant='standard'
                            error={getError('state')}
                            disabled={!isEditableField('state')}>
                            <InputLabel id='state-label'>
                              {t('createPayment.fields.stateProvince')}
                            </InputLabel>
                            <Select
                              labelId='state-label'
                              value={formValues.state || ''}
                              onChange={handleChange}
                              name='state'
                              className='full-width-elem'>
                              <MenuItem value=''>
                                {t(
                                  'createPayment.dropdowns.selectStateProvince',
                                )}
                              </MenuItem>
                              {states.map((state) => (
                                <MenuItem
                                  key={state.abbreviation}
                                  value={state.abbreviation}>
                                  {state.name}
                                </MenuItem>
                              ))}
                            </Select>
                            <FormHelperText style={helperTextStyle}>
                              {getErrorMessage('state')}
                            </FormHelperText>
                          </FormControl>
                        </div>
                      </GridItem>
                      <GridItem xs={12} sm={12} md={6}>
                        <CustomInput
                          labelText={t('createPayment.fields.zipPostalCode')}
                          id='refundToZip'
                          name='refundToZip'
                          value={formValues.refundToZip}
                          onChange={handleChange}
                          formControlProps={{ fullWidth: true }}
                          error={getError('refundToZip')}
                          inputProps={{
                            helperText: getErrorMessage('refundToZip'),
                          }}
                          helperTextStyle={helperTextStyle}
                          disabled={!isEditableField('refundToZip')}
                        />
                      </GridItem>
                    </GridContainer>
                    <GridContainer>
                      <ConditionalInput
                        fieldName='refund_amount'
                        refundModalType={refundModalType}
                        customPaymentType={selectedCustomPaymentType}
                        isCustomPaymentTypesEnabled={
                          isCustomPaymentTypesEnabled
                        }>
                        <GridItem xs={12} sm={12} md={6}>
                          <CustomInput
                            labelText={
                              <>
                                {t('createPayment.fields.paymentAmount')} (
                                {facilityCurrency})
                                <span style={asteriskStyle}>*</span>
                              </>
                            }
                            id='refundAmount'
                            name='refundAmount'
                            value={formValues.refundAmount}
                            onChange={handleChange}
                            formControlProps={{ fullWidth: true }}
                            error={getError('refundAmount')}
                            inputProps={{
                              helperText: getErrorMessage('refundAmount'),
                            }}
                            helperTextStyle={helperTextStyle}
                            disabled={!isEditableField('refundAmount')}
                          />
                        </GridItem>
                      </ConditionalInput>
                    </GridContainer>
                    <GridContainer>
                      <ConditionalInput
                        fieldName='general_ledger'
                        refundModalType={refundModalType}
                        customPaymentType={selectedCustomPaymentType}
                        isCustomPaymentTypesEnabled={
                          isCustomPaymentTypesEnabled
                        }>
                        <GridItem xs={12} sm={12} md={6}>
                          <CustomInput
                            labelText={getFieldLabel(
                              t('createPayment.fields.generalLedger'),
                              'general_ledger',
                              selectedCustomPaymentType,
                            )}
                            id='generalLedger'
                            name='generalLedger'
                            value={formValues.generalLedger}
                            onChange={handleChange}
                            formControlProps={{ fullWidth: true }}
                            error={getError('generalLedger')}
                            inputProps={{
                              helperText: getErrorMessage('generalLedger'),
                            }}
                            helperTextStyle={helperTextStyle}
                            disabled={!isEditableField('generalLedger')}
                          />
                        </GridItem>
                      </ConditionalInput>
                      <ConditionalInput
                        fieldName='memo'
                        refundModalType={refundModalType}
                        customPaymentType={selectedCustomPaymentType}
                        isCustomPaymentTypesEnabled={
                          isCustomPaymentTypesEnabled
                        }>
                        <GridItem xs={12} sm={12} md={6}>
                          <CustomInput
                            labelText={getFieldLabel(
                              t('createPayment.fields.checkMemo'),
                              'memo',
                              selectedCustomPaymentType,
                            )}
                            id='memo'
                            name='memo'
                            value={formValues.memo}
                            onChange={handleChange}
                            formControlProps={{ fullWidth: true }}
                            error={getError('memo')}
                            inputProps={{
                              helperText: getErrorMessage('memo'),
                            }}
                            helperTextStyle={helperTextStyle}
                            helperText={'test'}
                            disabled={!isEditableField('memo')}
                          />
                        </GridItem>
                      </ConditionalInput>
                      <ConditionalInput
                        fieldName='client_transaction_id'
                        refundModalType={refundModalType}
                        customPaymentType={selectedCustomPaymentType}
                        isCustomPaymentTypesEnabled={
                          isCustomPaymentTypesEnabled
                        }>
                        <GridItem xs={12} sm={12} md={6}>
                          <CustomInput
                            labelText={getFieldLabel(
                              t('createPayment.fields.clientTransactionId'),
                              'client_transaction_id',
                              selectedCustomPaymentType,
                            )}
                            id='clientTransactionId'
                            name='clientTransactionId'
                            value={formValues.clientTransactionId}
                            onChange={handleChange}
                            formControlProps={{ fullWidth: true }}
                            error={getError('clientTransactionId')}
                            inputProps={{
                              helperText: getErrorMessage(
                                'clientTransactionId',
                              ),
                            }}
                            helperTextStyle={helperTextStyle}
                            disabled={!isEditableField('clientTransactionId')}
                          />
                        </GridItem>
                      </ConditionalInput>
                      <ConditionalInput
                        fieldName='vendor_id_number'
                        refundModalType={refundModalType}
                        customPaymentType={selectedCustomPaymentType}
                        isCustomPaymentTypesEnabled={
                          isCustomPaymentTypesEnabled
                        }>
                        <GridItem xs={12} sm={12} md={6}>
                          <CustomInput
                            labelText={getFieldLabel(
                              t('createPayment.fields.vendorIdNumber'),
                              'vendor_id_number',
                              selectedCustomPaymentType,
                            )}
                            id='vendorIdNumber'
                            name='vendorIdNumber'
                            value={formValues.vendorIdNumber}
                            onChange={handleChange}
                            formControlProps={{ fullWidth: true }}
                            error={getError('vendorIdNumber')}
                            inputProps={{
                              helperText: getErrorMessage('vendorIdNumber'),
                            }}
                            helperTextStyle={helperTextStyle}
                            disabled={!isEditableField('vendorIdNumber')}
                          />
                        </GridItem>
                      </ConditionalInput>
                      <ConditionalInput
                        fieldName='payee_account_number'
                        refundModalType={refundModalType}
                        customPaymentType={selectedCustomPaymentType}
                        isCustomPaymentTypesEnabled={
                          isCustomPaymentTypesEnabled
                        }>
                        <GridItem xs={12} sm={12} md={6}>
                          <CustomInput
                            labelText={getFieldLabel(
                              t('createPayment.fields.payeeAccountNumber'),
                              'payee_account_number',
                              selectedCustomPaymentType,
                            )}
                            id='payeeAccountNumber'
                            name='payeeAccountNumber'
                            value={formValues.payeeAccountNumber}
                            onChange={handleChange}
                            formControlProps={{ fullWidth: true }}
                            error={getError('payeeAccountNumber')}
                            inputProps={{
                              helperText: getErrorMessage('payeeAccountNumber'),
                            }}
                            helperTextStyle={helperTextStyle}
                            disabled={!isEditableField('payeeAccountNumber')}
                          />
                        </GridItem>
                      </ConditionalInput>
                    </GridContainer>
                    <GridContainer>
                      {businessUnits.length > 1 && (
                        <GridItem xs={12} sm={12} md={6}>
                          <div className='custom-select'>
                            <FormControl
                              variant='standard'
                              error={getError('businessUnitId')}
                              disabled={!isEditableField('businessUnitId')}>
                              <InputLabel id='business-unit-name-label'>
                                <>
                                  {t('createPayment.fields.businessUnitName')}
                                  <span style={asteriskStyle}>*</span>
                                </>
                              </InputLabel>
                              <Select
                                labelId='business-unit-name-label'
                                value={formValues.businessUnitId || ''}
                                onChange={handleChange}
                                name='businessUnitId'
                                className='full-width-elem'>
                                {businessUnits.map((unit) => (
                                  <MenuItem key={unit.id} value={unit.id}>
                                    {unit.name}
                                  </MenuItem>
                                ))}
                              </Select>
                              <FormHelperText style={helperTextStyle}>
                                {getErrorMessage('businessUnitId')}
                              </FormHelperText>
                            </FormControl>
                          </div>
                        </GridItem>
                      )}
                      {shouldShowBankAccount &&
                        organizationId &&
                        !bankAccountsLoading && (
                          <>
                            {activeBankAccounts.length === 0 ? (
                              <GridItem xs={12} sm={12} md={12}>
                                <Alert severity='error' sx={{ my: 2 }}>
                                  {t(
                                    'createPayment.errors.noActiveBankAccounts',
                                  )}
                                </Alert>
                              </GridItem>
                            ) : (
                              <GridItem xs={12} sm={12} md={6}>
                                <div className='custom-select'>
                                  <FormControl
                                    variant='standard'
                                    error={getError('clientBankAccountId')}
                                    disabled={
                                      !isEditableField('clientBankAccountId') ||
                                      activeBankAccounts.length === 1
                                    }>
                                    <InputLabel id='client-bank-account-label'>
                                      <>
                                        {t(
                                          'createPayment.fields.clientBankAccount',
                                        )}
                                        {activeBankAccounts.length > 1 && (
                                          <span style={asteriskStyle}>*</span>
                                        )}
                                      </>
                                    </InputLabel>
                                    <Select
                                      labelId='client-bank-account-label'
                                      value={
                                        formValues.clientBankAccountId || ''
                                      }
                                      onChange={handleChange}
                                      name='clientBankAccountId'
                                      className='full-width-elem'>
                                      {activeBankAccounts.map((account) => (
                                        <MenuItem
                                          key={account.id}
                                          value={account.id}>
                                          {account.display_name || ''}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                    <FormHelperText style={helperTextStyle}>
                                      {getErrorMessage('clientBankAccountId')}
                                    </FormHelperText>
                                  </FormControl>
                                </div>
                              </GridItem>
                            )}
                          </>
                        )}
                      {showAudienceField && audiences.length > 0 && (
                        <GridItem xs={12} sm={12} md={6}>
                          <div className='custom-select'>
                            <FormControl
                              variant='standard'
                              error={getError('audienceId')}
                              disabled={!isEditableField('audienceId')}>
                              <InputLabel id='audience-label'>
                                {t('createPayment.fields.audience')}
                              </InputLabel>
                              <Select
                                labelId='audience-label'
                                value={formValues.audienceId || ''}
                                onChange={handleChange}
                                name='audienceId'
                                className='full-width-elem'>
                                {audiences.map((aud) => (
                                  <MenuItem key={aud.id} value={aud.id}>
                                    {aud.name}
                                  </MenuItem>
                                ))}
                              </Select>
                              <FormHelperText style={helperTextStyle}>
                                {getErrorMessage('audienceId')}
                              </FormHelperText>
                            </FormControl>
                          </div>
                        </GridItem>
                      )}
                    </GridContainer>
                    <GridContainer>
                      <GridItem xs={6} sm={6} md={6}>
                        <CpdCheckboxCard
                          checked={formValues.payImmediately || false}
                          onChange={(checked) =>
                            handleChange({
                              target: {
                                name: 'payImmediately',
                                value: checked,
                              },
                            })
                          }
                          label={t('createPayment.immediatePayment.title')}
                          description={t(
                            'createPayment.immediatePayment.description',
                          )}
                          disabled={!isEditable}
                          error={getError('payImmediately')}
                          errorMessage={getErrorMessage('payImmediately')}
                          sx={{ mb: 2 }}
                        />
                        {formValues.payImmediately && isEcheckWorkflowEnabled && (
                          <CheckTypeSelector
                            value={formValues.checkType}
                            onChange={(value) =>
                              handleChange({
                                target: { name: 'checkType', value },
                              })
                            }
                            disabled={!isEditableField('payImmediately')}
                            error={getError('checkType')}
                            errorMessage={getErrorMessage('checkType')}
                          />
                        )}
                      </GridItem>
                    </GridContainer>
                    <GridContainer>
                      <GridItem xs={12} sm={12} md={12}>
                        <LineItemsSection
                          lineItems={formValues.lineItems}
                          onChange={handleLineItemsChange}
                          disabled={!isEditableField('lineItems')}
                          errors={errors}
                          refundModalType={refundModalType}
                          customPaymentType={selectedCustomPaymentType}
                          selectedFacility={selectedFacility}
                        />
                      </GridItem>
                    </GridContainer>
                    {refund && !isApprovalPage && (
                      <GridContainer>
                        <GridItem xs={6} sm={6} md={6}>
                          <CustomInput
                            labelText={
                              <>
                                {t('createPayment.fields.comment')}
                                <span style={asteriskStyle}>*</span>
                              </>
                            }
                            id='edit-comment'
                            name='edit-comment'
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            formControlProps={{ fullWidth: true }}
                            inputProps={{
                              placeholder: t(
                                'createPayment.placeholders.commentPlaceholder',
                              ),
                            }}
                            helperTextStyle={helperTextStyle}
                            disabled={!isEditableField('comment')}
                          />
                        </GridItem>
                      </GridContainer>
                    )}
                    {(isCustomPaymentTypesEnabled
                      ? selectedCustomPaymentType?.enableInternalAttachments ||
                        selectedCustomPaymentType?.enablePayeeAttachments
                      : showAttachments) && (
                      <GridContainer>
                        {
                          <GridItem xs={12} sm={12} md={12}>
                            <ManageFiles
                              refund={refund}
                              onChange={handleAttachmentsChange}
                              manualRefund={true}
                              disabled={
                                !isEditableField('internalFileCount') ||
                                !isEditableField('payeeFileCount')
                              }
                              isInternalAttachmentsEnabled={
                                isCustomPaymentTypesEnabled
                                  ? selectedCustomPaymentType?.enableInternalAttachments
                                  : false
                              }
                              isPayeeAttachmentsEnabled={
                                isCustomPaymentTypesEnabled
                                  ? selectedCustomPaymentType?.enablePayeeAttachments
                                  : true
                              }
                              isCustomPaymentTypesEnabled={
                                isCustomPaymentTypesEnabled
                              }
                            />
                            <FormHelperText style={errorHelperTextStyle}>
                              {getErrorMessage('internalFileCount')}
                            </FormHelperText>
                            <FormHelperText style={errorHelperTextStyle}>
                              {getErrorMessage('payeeFileCount')}
                            </FormHelperText>
                          </GridItem>
                        }
                      </GridContainer>
                    )}
                  </>
                )}
                {!(refund && isApprovalPage) && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      gap: '12px',
                    }}>
                    {resendCommunicationUi && refund && (
                      <label
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          cursor: 'pointer',
                        }}>
                        <input
                          type='checkbox'
                          checked={resendCommunication}
                          onChange={(e) =>
                            setResendCommunication(e.target.checked)
                          }
                        />
                        {t(
                          'createPayment.buttons.resendCommunication',
                          'Resend Communication',
                        )}
                      </label>
                    )}
                    <LoadingButton
                      color='rose'
                      type='submit'
                      isLoading={isLoading}
                      style={{ width: '150px' }}
                      disabled={!isEditable}>
                      {refund
                        ? t('createPayment.buttons.updatePayment')
                        : t('createPayment.buttons.createPayment')}
                    </LoadingButton>
                  </div>
                )}
              </CardBody>
            </div>
          </Card>
        </GridItem>
      </GridContainer>
      <SearchSuppliersModal
        open={suppliersModalOpen}
        onClose={() => setSuppliersModalOpen(false)}
        onSelectSupplier={(patch) =>
          setFormValues((prev) => ({ ...prev, ...patch }))
        }
      />
    </form>
  );
};

export default CreateOrEditManualRefund;
